import { useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AxiosInstance from '../configs/axiosConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

export const useAxiosInterceptors = () => {
    const { showError } = useSnackbar();
    const { oktaAuth, authState } = useOktaAuth();
    
    // Use refs to maintain current values without causing re-registration
    const authStateRef = useRef(authState);
    const oktaAuthRef = useRef(oktaAuth);
    const interceptorsSetup = useRef(false);
    
    // Update refs when values change
    authStateRef.current = authState;
    oktaAuthRef.current = oktaAuth;

    useEffect(() => {
        // Only set up interceptors once, regardless of oktaAuth changes
        if (interceptorsSetup.current) {
            console.log('⏭️ Interceptors already setup, skipping');
            return;
        }

        interceptorsSetup.current = true;

        // Request interceptor to add auth token
        const requestInterceptor = AxiosInstance.interceptors.request.use(
            async (config) => {
                // Use current refs to get the latest auth state
                const currentAuthState = authStateRef.current;
                const currentOktaAuth = oktaAuthRef.current;

                // Only add authorization header if we have an authenticated state
                if (currentAuthState?.isAuthenticated && currentOktaAuth) {
                    try {
                        // Get access token from oktaAuth
                        const accessToken = await currentOktaAuth.getAccessToken();
                        
                        if (accessToken) {
                            // Set the authorization header
                            config.headers = config.headers || {};
                            config.headers['Authorization'] = `Bearer ${accessToken}`;
                        } else {
                            console.warn('⚠️ Access token is null or undefined for', config.url);
                        }
                    } catch (error) {
                        console.error('❌ Error getting access token for', config.url, error);
                    }
                } else {
                    console.log('🚫 Not authenticated or missing auth instance for', config.url);
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for global error handling
        const responseInterceptor = AxiosInstance.interceptors.response.use(
            (response) => {
                // Any status code that lies within the range of 2xx causes this function to trigger
                return response;
            },
            (error) => {
                // Any status codes that falls outside the range of 2xx causes this function to trigger
                let errorMessage = 'An unexpected error occurred';

                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    const status = error.response.status;
                    const statusText = error.response.statusText;
                    const data = error.response.data;

                    switch (status) {
                        case 400:
                            errorMessage = data?.message || 'Bad request. Please check your input.';
                            break;
                        case 401:
                            errorMessage = 'Unauthorized. Please check your credentials.';
                            break;
                        case 403:
                            errorMessage = 'Forbidden. You do not have permission to perform this action.';
                            break;
                        case 404:
                            errorMessage = 'Resource not found.';
                            break;
                        case 500:
                            errorMessage = 'Internal server error. Please try again later.';
                            break;
                        default:
                            errorMessage = data?.message || statusText || `Error ${status}: Something went wrong`;
                    }
                } else if (error.request) {
                    // The request was made but no response was received
                    errorMessage = 'Network error. Please check your connection and try again.';
                } else {
                    // Something happened in setting up the request that triggered an Error
                    errorMessage = error.message || 'Request failed. Please try again.';
                }

                // Only show global error for certain cases
                // You might want to exclude specific endpoints or error types
                if (!error.config?.skipGlobalError) {
                    showError(errorMessage);
                }

                return Promise.reject(error);
            }
        );

        // Cleanup function to remove interceptors when component unmounts
        return () => {
            if (requestInterceptor !== undefined) {
                AxiosInstance.interceptors.request.eject(requestInterceptor);
                console.log('🧹 Cleaned up request interceptor');
            }
            if (responseInterceptor !== undefined) {
                AxiosInstance.interceptors.response.eject(responseInterceptor);
                console.log('🧹 Cleaned up response interceptor');
            }
            interceptorsSetup.current = false;
        };
    }, []); // Empty dependency array - set up only once
};
